import useServiceType from '@/hooks/useServiceType';
import { UserInfoState } from '@/mobile/states/userInfoState';
import { CollectionInfoState, DeliveryInfoState } from '@/states/deviceDeliCollInfoState';
import { EntryOptionInfoState } from '@/states/entryOptionInfoState';
import { QuitOptionInfoState } from '@/states/quitOptionInfoState';
import { serviceIdState, serviceInfoState } from '@/states/serviceInfoState';
import url from '@/utils/config';
import { Constants } from '@/utils/constants';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

/**
 * 「パンくずリスト」に表示する情報定義。
 */
export type BreadcrumbType = {
  /**
   * 対象階層。
   * 使用例は、「/option/voip」のパスで「voip」の表示だけを変更する場合は、「1」を指定する。
   */
  depth: number;
  /**
   * 表示名。
   * 未指定の場合は、パスに従った名称で表示する。
   */
  title?: string;
  /**
   * リンク先。
   * 未指定の場合は、リンク表示しない。
   */
  path?: string;
};

/**
 * コンポーネントのパラメータ定義。
 */
type Props = {
  // パンくずリストの表示情報
  // eslint-disable-next-line react/require-default-props
  breadcrumbs?: BreadcrumbType[];
};

/**
 * パンくずリスト表示用のコンポーネント。
 * @param param0 コンポーネント用パラメータ
 * @returns 画面表示
 */
const Breadcrumb = ({ breadcrumbs = [] }: Props) => {
  const [paramList, setParmList] = useState<string[]>([]);
  const router = useRouter();
  const entryOptionInfo = useRecoilValue(EntryOptionInfoState);
  const quitOptionInfo = useRecoilValue(QuitOptionInfoState);
  const deliveryInfo = useRecoilValue(DeliveryInfoState);
  const collection = useRecoilValue(CollectionInfoState);
  const userInfo = useRecoilValue(UserInfoState);

  const serviceInfo = useRecoilValue(serviceInfoState);
  const serviceId = useRecoilValue(serviceIdState);
  const serviceType = useServiceType();
  const thisServices = serviceInfo.find(({ service_id }) => service_id === serviceId);
  // NUROモバイルでパスワードが初期パスワードのままの場合は、フッターを表示しない
  const isInitialPassword =
    serviceType === Constants.ServiceType.MOBILE &&
    thisServices?.password_setter === Constants.PasswordSetter.SYSTEM;
  // `isSetting` は、初期パスワードが設定されていれば `false` になる
  const isSetting: boolean = !isInitialPassword;

  // 年月の正規表現。存在可否は見ていない。
  const dayPattern = /^[0-9]{4}-(0[1-9]|1[0-2])$/;

  /**
   * URL セグメントを画面表示用の日本語タイトルへ変換する。
   * 動的オプション名や年月明細のような例外もここで吸収し、呼び出し側を単純に保つ。
   */
  const toPageName = (path: string) => {
    // ご利用料金明細だけ成形が必要
    if (dayPattern.test(path)) {
      return `${path.replace('-0', '年').replace('-', '年')}月ご利用料金明細`;
    }

    switch (path) {
      case 'top':
        return '料金・お知らせ';
      case 'billing':
        return 'ご利用金額明細';
      case 'invoice':
        return 'ご利用金額明細';
      case 'history':
        return 'ご利用料金・お支払い状況一覧';
      case 'contract':
        return 'ご契約情報';
      case 'mail':
        return 'メールアドレスの登録・変更';
      case 'contractor':
        return '契約者情報の変更';
      case 'confirm':
        return '入力内容の確認';
      case 'option':
        return 'ご契約オプション';
      case 'entry':
        return `${entryOptionInfo.option_name} 申込`;
      case 'quit':
        return `${quitOptionInfo.option_name} 解約`;
      case 'payment':
        return 'お支払い情報の登録・変更';
      case 'credit_card':
        return 'クレジットカード情報の登録・変更';
      case 'softbank':
        return 'ソフトバンクまとめて支払い情報の確認・変更';
      case 'account_transfer':
        return '口座振替情報の登録・変更';
      case 'docomo':
        return 'NTTドコモ合算請求情報の登録・変更';
      case 'promotion':
        return 'プロモーション情報配信設定';
      case 'campaign':
        return '特典・キャンペーン情報';
      case 'detail':
        return 'キャンペーン詳細';
      case 'id_password':
        return 'ユーザーID/パスワードの変更';
      case 'password':
        return 'パスワードの変更';
      case 'error':
        return 'エラー';
      case 'voip':
        return 'NURO 光 でんわ';
      case 'wc24':
        return 'ホワイトコール24';
      case 'web_detail':
        return 'Web明細ログインID/パスワード再発行';
      case 'change':
        return '付加サービス変更';
      case 'construction':
        return '回線引込み工事承諾';
      case 'cashback_register':
        return 'キャッシュバック口座登録';
      case 'move_token':
        return '移転(お引越し)の手続き';
      case 'kddi':
        return 'au PAY（auかんたん決済）情報の確認・変更';
      case 'secom':
        return 'セコム駆けつけ';
      case 'bravia_recycle':
        return 'ブラビア 月額オプション リサイクル申込';
      case 'input':
        return '入力';
      case 'entry_cancel':
        return '申込キャンセル';
      case 'poreport':
        return 'Hands-up 活動報告書';
      case 'quit_cancel':
        return '回線のご解約';
      case 'login_history':
        return 'ログイン履歴';
      case 'billing_address':
        return '請求書送付先住所の変更';
      case 'quit_contract':
        return 'NURO 光 回線のご解約';
      case 'step1_enter':
        return 'ご解約方法の入力';
      case 'step2_confirm':
        return '解約に関わる費用を確認する';
      case 'step3_confirm':
        return 'ご解約内容の確認';
      case 'sony_account':
        return 'ソニーアカウント';
      case 'withdraw':
        return 'サービス解約';
      case 'notification_history':
        return '通知履歴';
      case 'enter':
        return '申込';
      case 'form':
        return '入力';
      case 'nuro_denki':
        return 'NURO でんき';
      case 'plan_change':
        return `プラン変更`;
      case 'g10upgrade':
        return 'アップグレード申込説明';
      case 'areacheck':
        return '提供エリア判定';
      case 'result':
        return '受付条件判定・プラン選択';
      case 'applicationplan':
        return '申込プラン確認';
      case 'agreement':
        return '注意事項・規約同意';
      case 'completed':
        return '申込完了';
      case 'mobile':
        return '料金・お知らせ';
      case 'telephone':
        return '音声通話サービス';
      case 'reimbursement':
        return '返還請求明細確認';
      case 'phone_protection_quit':
        return '端末補償 解約';
      case 'calling':
        return '通話料・SMS通信料明細';
      case 'virus_buster_mobile_entry':
        return 'ウイルスバスター モバイル 月額版 申込';
      case 'virus_buster_mobile_quit':
        return 'ウイルスバスター モバイル 月額版 解約';
      case 'charge':
        return 'チャージ';
      case 'charge_neo_trial':
        return 'NEOトライアル';
      case 'phone_protection_imei':
        return '端末補償 IMEI変更';
      case 'value_data_free_plan_entry':
        return 'バリューデータフリー';
      case 'network_fifth_generation_entry':
        return '5Gオプション';
      case 'network_fifth_generation_quit':
        return '5Gオプション';
      case 'i_filter_entry':
        return 'i-フィルター for マルチデバイス';
      case 'purchase_device_entry':
        return '端末買取サービス';
      case 'mnp':
        return 'MNP開通予約';
      case 'data_advance':
        return 'データ前借り';
      case 'packet_gift':
        return 'パケットギフト';
      case 'plan_entry':
        return 'プラン変更';
      case 'plan_cancel':
        return 'プラン変更キャンセル';
      case 'esim_profile':
        return 'eSIMプロファイルダウンロード';
      case 'esim':
        return 'eSIM交換（再発行）';
      case 'device':
        return '端末購入';
      case 'traffic_control':
        return '通信最適化';
      case 'procedure':
        return '手続き履歴';
      case 'value_data_free_plan_quit':
        return 'バリューデータフリー';
      case 'cashback':
        return 'キャッシュバック受取手続き';
      case 'mobile/login_history':
        return 'ログイン履歴';
      case 'mobile/notification_history':
        return '通知履歴';
      case 'mobile/top':
        return '料金・お知らせ';
      case 'mobile/contract':
        return 'ご契約情報';
      case 'mobile/option':
        return 'ご契約オプション';
      case 'mobile/campaign':
        return '特典・キャンペーン情報';
      case 'usage_status':
        return 'ご利用状況';
      case 'receipt':
        return '受取';
      case 'giga_plus':
        return 'Gigaプラス';
      case 'delivery_setting':
        return '入力';
      case 'confirmMnp':
        return '入力内容の確認';
      case 'delivery':
        return 'ご利用機器情報';
      case 'change_delivery':
        if (deliveryInfo.redelivery_flg === '0') {
          return '配送希望日の変更';
        }
        return '再配送希望日の申込';

      case 'change_collection':
        if (collection.recollection_flg === '0') {
          return '返却予定日の変更';
        }
        return '再集荷希望日の申込';
      case 'sim_stop':
        return '利用停止';
      case 'sim_restart':
        return '利用再開';
      case 'user_change':
        return userInfo.user_type === '0' ? '利用者情報の登録' : '利用者情報の変更';
      case 'user_type':
        return '利用者の選択';
      default:
        return path;
    }
  };

  // Link用のparameter成形
  const decorateURL = (index: number) => {
    return paramList.slice(0, index + 1).join('/');
  };

  // 現在のページとご利用料金明細以外のリンク遷移設定
  const decorateBreadcrumbURL = (value: string, index: number) => {
    if (breadcrumbs) {
      const breadcrumb = breadcrumbs.find((e) => {
        return e.depth === index;
      });
      if (breadcrumb) {
        const title = breadcrumb.title ? breadcrumb.title : toPageName(value);
        if (!breadcrumb.path) {
          return <span>{title}</span>;
        }
        return (
          <span>
            <Link href={breadcrumb.path} legacyBehavior>
              {title}
            </Link>
          </span>
        );
      }
    }
    if (paramList.length === index + 1 || value === 'billing') {
      return <span>{toPageName(value)}</span>;
    }
    return (
      <span>
        <Link href={`/${decorateURL(index)}`} legacyBehavior>
          {toPageName(value)}
        </Link>
      </span>
    );
  };

  // 下層を削除するpath
  const lowerStrata = ['move_token', 'poreport'];
  useEffect(() => {
    // pathを配列にする
    const setList = decodeURI(router.asPath).substring(1).split('/');
    // 末尾に空文字が入る為消去
    setList.pop();

    if (setList[0] === 'mobile') {
      setList[0] = `${setList[0]}/${setList[1]}`;
      setList.splice(1, 1);
    }

    // 下層を削除
    let isLowerStrata = false;
    const index = setList.findIndex((list) => {
      isLowerStrata = lowerStrata.some((lower) => list === lower);
      return isLowerStrata;
    });
    if (isLowerStrata) setList.splice(index + 1, 1);

    setParmList(setList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <nav aria-label='Breadcrumb' className='breadcrumb'>
      {isSetting && (
        <ol className='breadcrumb__list'>
          <li>
            <Link href={`${serviceType === Constants.ServiceType.MOBILE ? '/mobile/top' : '/top'}`}>
              <img
                src={url('/images/icon_home_black.png')}
                className='bread__homeIcon'
                alt='ホーム'
              />
            </Link>
          </li>
          {paramList.map((value: string, index: number) => (
            <li key={value} aria-current={index === paramList.length - 1 ? 'page' : undefined}>
              <span className='breadcrumb__separator' />
              {decorateBreadcrumbURL(value, index)}
            </li>
          ))}
        </ol>
      )}
    </nav>
  );
};

export default Breadcrumb;
